/**
 * DEPTH SELECT (doc 11 §Screen map). All three rungs of the trench.
 *
 * Two gates live here, both from doc 09/12: a rung opens only when the rung
 * above it has been cleared, and a colony runs one descent at a time — starting
 * a second would quietly overwrite the saved one at its next payday.
 */

import { L, S } from '../../content/strings.ts';
import { assistCoreMult, effectsOf } from '../../meta/effects.ts';
import { activeTiers } from '../../meta/pressure.ts';
import { font } from '../../render/font/font.ts';
import { DEPTHS, DEPTH_IDS } from '../../render/palette.ts';
import { mix, PixelSurface } from '../../render/surface.ts';
import type { FrameOpts, MenuItem } from '../../ui/menu.ts';
import { g, UI } from '../../ui/theme.ts';
import { drawStatRow, type Rect } from '../../ui/widgets.ts';
import { B, RES_IDS, STORAGE_BASE } from '../../sim/defs.ts';
import { scenarioFor, skeletonScenario } from '../../sim/scenario.ts';
import type { Resources } from '../../sim/types.ts';
import type { App } from '../app.ts';
import { RunScreen } from '../run.ts';
import { MenuScreen } from './menuscreen.ts';
import { PressureScreen } from './pressure.ts';
import { ResultsScreen } from './results.ts';
import { TitleScreen } from './title.ts';

export class DepthScreen extends MenuScreen {
  protected frame(): FrameOpts {
    const run = this.app.profile?.run;
    return {
      title: S.depth.trench,
      subtitle: run ? S.depth.runWaitingDown : S.depth.pickPressure,
      footer: S.depth.enterDescendsP,
      stamp: this.app.profile ? L.pearls(this.app.profile.pearls) : undefined,
    };
  }

  protected override columnWidth(body: Rect): number {
    return Math.min(320, Math.floor(body.w * 0.5));
  }

  /** Doc 09's ladder: a rung opens when the rung above it has been cleared. */
  private unlocked(depth: number): boolean {
    if (depth === 1) return true;
    return this.app.profile?.depthsCleared.includes(depth - 1) ?? false;
  }

  protected items(): MenuItem[] {
    const run = this.app.profile?.run;
    const cleared = this.app.profile?.depthsCleared ?? [];
    const row = (depth: number, name: string, hint: string): MenuItem => {
      const resuming = run?.depth === depth;
      const item: MenuItem = {
        label: resuming ? `D${depth} · ${name} — RESUME` : `D${depth} · ${name}`,
        hotkey: String(depth),
        hint: resuming ? `${run!.fightsCleared} FIGHTS CLEARED · PICKS UP AT THE LAST PAYDAY` : hint,
        tone: 'good',
      };
      if (!this.unlocked(depth)) {
        item.disabled = true;
        item.hint = `CLEAR D${depth - 1} FIRST. THE TRENCH IS A LADDER, NOT A MENU.`;
        item.value = S.depth.locked;
        delete item.tone;
      } else if (run && !resuming) {
        // one colony, one run in progress (doc 12 §Saves) — starting a second
        // would quietly overwrite the first at its next payday
        item.disabled = true;
        item.hint = `A RUN IS WAITING ON D${run.depth}. FINISH IT, OR LOSE IT.`;
        item.value = S.depth.busy;
        delete item.tone;
      } else if (cleared.includes(depth)) {
        const tiers = activeTiers(this.app.profile?.pressure[String(depth)] ?? []).length;
        item.value = tiers > 0 ? `PRESSURE ×${tiers}` : S.depth.cleared;
        if (tiers > 0) item.tone = 'critical';
      }
      return item;
    };
    return [
      row(1, S.depth.shelf, S.depth.shoalsOldSnapjaw),
      row(2, S.depth.gloom, S.depth.spawnEdgesCloaked),
      row(3, S.depth.floor, S.depth.spawnEdgesThings),
    ];
  }

  protected activate(index: number): void {
    const depth = index + 1;
    if (!this.unlocked(depth)) return;
    const profile = this.app.profile;
    if (profile?.run && profile.run.depth !== depth) return;
    const scenario = scenarioFor(depth);
    const eff = profile ? effectsOf(profile.owned) : null;
    if (eff) {
      for (const res of RES_IDS) {
        scenario.startingKit[res] += eff.kitBonus[res as keyof Resources] ?? 0;
      }
    }
    const saved = profile?.run?.depth === depth ? profile.run : undefined;
    const seed = saved?.seed ?? Number(new URLSearchParams(location.search).get('seed')) ?? 0;
    this.app.go(
      (a) =>
        new RunScreen(a, {
          scenario,
          seed: seed || 20260824,
          ...(saved ? { snapshot: saved.snapshot } : {}),
          onExit: (result) => {
            if (result) a.go((b) => new ResultsScreen(b, result));
            else a.go((b) => new TitleScreen(b));
          },
        }),
    );
  }

  protected override back(): void {
    this.app.go((a) => new TitleScreen(a));
  }

  /** Doc 08: Pressure is a post-victory system, so the key only works there. */
  protected override key(k: string): void {
    if (k !== 'p') return;
    const depth = this.selected + 1;
    if (!(this.app.profile?.depthsCleared.includes(depth) ?? false)) return;
    this.app.go((a) => new PressureScreen(a, depth));
  }

  /** A cross-section of the trench, plus what you are descending with. */
  protected override detail(s: PixelSurface, body: Rect): void {
    const x = body.x + this.columnWidth(body) + g(6);
    const w = Math.min(320, body.x + body.w - x);
    if (w < 150) return;
    const h = Math.min(body.h - g(10), 150);
    const rows = this.rects.length;
    for (let i = 0; i < DEPTH_IDS.length; i++) {
      const pal = DEPTHS[DEPTH_IDS[i]!]!;
      const y0 = body.y + Math.floor((h / 3) * i);
      const y1 = body.y + Math.floor((h / 3) * (i + 1));
      for (let y = y0; y < y1; y++) {
        const k = (y - y0) / Math.max(1, y1 - y0);
        s.hline(x, x + w - 1, y, mix(pal.waterTop, pal.waterBottom, k), 'set');
      }
      const lit = i < rows && i === this.selected;
      s.strokeRect(x, y0, w, y1 - y0, lit ? UI.accent : UI.panelRimDark);
      font('micro').draw(s, pal.name, x + 4, y0 + 4, lit ? UI.ink : UI.inkDim, { caps: true, letterSpacing: 1 });
    }

    const profile = this.app.profile;
    if (!profile) return;
    const eff = effectsOf(profile.owned);
    const kit = skeletonScenario().startingKit;
    let y = body.y + h + g(4);
    font('header').draw(s, S.depth.descendingWith, x, y, UI.ink, { caps: true, letterSpacing: 1 });
    y += font('header').capHeight + g(2);
    const bottom = body.y + body.h;
    const row = (label: string, value: string): void => {
      if (y + 12 > bottom) return; // short window: clip the tail, never spill
      drawStatRow(s, { x, y, w, h: 8 }, label, value);
      y += 12;
    };
    row(S.depth.kit, `${kit.ore + (eff.kitBonus.ore ?? 0)} ORE · ${kit.alloy + (eff.kitBonus.alloy ?? 0)} ALLOY · ${kit.ammo} AMMO`);
    row(S.depth.coreHp, `${Math.round(B.core_dome.hp * eff.coreHpMult * assistCoreMult(profile))}`);
    row(S.depth.storageCap, `${Math.round(STORAGE_BASE * eff.storageMult)}/RES`);
    row(S.depth.towers, `${3 + eff.towers.length} UNLOCKED`);
  }
}
